/**
 * RefillVerifyModal — flow that asks the veteran to scan the pickup bottle,
 * compares the barcode against the value saved on the prescription, and
 * routes them to a match / mismatch / no-saved-barcode outcome.
 *
 *   match              → green Confirmed badge, auto-bump lastFilled to today
 *                        in the user's local tz, decrement refillsRemaining.
 *   mismatch           → red banner with two CTAs:
 *                        (a) Replace barcode and notify pharmacist
 *                        (b) Cancel (this is a different prescription)
 *   no-saved-barcode   → soft yellow callout, offer to save the scanned
 *                        value as the new canonical barcode for next time.
 *
 * The compare + patch-building logic lives in `lib/refillVerify.js` so it
 * can be unit-tested without rendering anything.
 */
import React, { useCallback, useMemo, useState } from "react";
import { CheckCircle, Warning, Barcode, Camera, X, Copy } from "@phosphor-icons/react";
import { decodeBarcodeDetailedFromFile } from "@/lib/barcodeScanner";
import LiveBarcodeScanner from "@/components/LiveBarcodeScanner";
import {
  compareBarcodes,
  buildRefillConfirmPatch,
  buildBarcodeReplacePatch,
} from "@/lib/refillVerify";
import { toLocalDateInputValue } from "@/lib/dateUtils";
import { showToast } from "@/lib/toast";

export default function RefillVerifyModal({ rx, onClose, onPatch }) {
  const [showLive, setShowLive] = useState(false);
  const [manualScan, setManualScan] = useState("");
  const [scannedFormat, setScannedFormat] = useState("");
  const [busy, setBusy] = useState(false);

  // Verdict re-computes synchronously whenever a scan lands.
  const verdict = useMemo(
    () => compareBarcodes(rx?.barcodeData, manualScan),
    [rx?.barcodeData, manualScan],
  );

  const handleFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const bc = await decodeBarcodeDetailedFromFile(f).catch(() => null);
      if (!bc?.text) {
        showToast({ variant: "warn", message: "Couldn't read a barcode from that image. Try the camera or paste it manually." });
        return;
      }
      setManualScan(bc.text);
      setScannedFormat(bc.format || "");
    } finally {
      setBusy(false);
      // Reset file input so re-uploading the same file fires onChange again
      if (e.target) e.target.value = "";
    }
  }, []);

  const handleLive = useCallback(({ barcode, barcodeFormat }) => {
    setShowLive(false);
    if (barcode) {
      setManualScan(barcode);
      setScannedFormat(barcodeFormat || "");
    }
  }, []);

  const confirmMatch = useCallback(async () => {
    setBusy(true);
    try {
      const patch = buildRefillConfirmPatch(rx, toLocalDateInputValue());
      await onPatch(rx.id, patch);
      showToast({ variant: "success", message: `Refill confirmed. ${patch.refillsRemaining} refill${patch.refillsRemaining === 1 ? "" : "s"} remaining.` });
      onClose();
    } catch {
      showToast({ variant: "error", message: "Couldn't save the refill confirmation. Try again." });
    } finally {
      setBusy(false);
    }
  }, [rx, onPatch, onClose]);

  const replaceBarcode = useCallback(async () => {
    setBusy(true);
    try {
      const patch = buildBarcodeReplacePatch({
        scannedBarcode: verdict.scannedNorm,
        scannedFormat,
      });
      await onPatch(rx.id, patch);
      showToast({ variant: "success", message: "Barcode replaced. Contact your pharmacist using the copy below." });
      onClose();
    } catch {
      showToast({ variant: "error", message: "Couldn't save the replacement barcode. Try again." });
    } finally {
      setBusy(false);
    }
  }, [rx?.id, verdict.scannedNorm, scannedFormat, onPatch, onClose]);

  const adoptFirstBarcode = useCallback(async () => {
    setBusy(true);
    try {
      const patch = {
        barcodeData: verdict.scannedNorm,
        barcodeFormat: scannedFormat || "",
        lastFilled: toLocalDateInputValue(),
        refillsRemaining: Math.max(0, (Number(rx.refillsRemaining) || 0) - 1),
        lastVerifiedAt: new Date().toISOString(),
        lastVerifyVerdict: "match",
      };
      await onPatch(rx.id, patch);
      showToast({ variant: "success", message: "Barcode saved + refill confirmed." });
      onClose();
    } catch {
      showToast({ variant: "error", message: "Couldn't save. Try again." });
    } finally {
      setBusy(false);
    }
  }, [rx, verdict.scannedNorm, scannedFormat, onPatch, onClose]);

  const pharmacistMessage = useMemo(() => {
    if (verdict.verdict !== "mismatch") return "";
    return [
      `Hello — I am verifying a prescription refill and the barcode on the bottle does not match what's on file.`,
      `Medication: ${rx?.medName || "(not set)"}`,
      `Rx number on file: ${rx?.rxNumber || "(not set)"}`,
      `Barcode on file: ${verdict.savedNorm}`,
      `Barcode on the new bottle: ${verdict.scannedNorm}`,
      `Pharmacy: ${rx?.pharmacy || "(not set)"}`,
      `Please confirm whether this is correct, a re-label, or a dispensing error. Thank you.`,
    ].join("\n");
  }, [verdict, rx]);

  if (showLive) {
    return <LiveBarcodeScanner onCapture={handleLive} onClose={() => setShowLive(false)} />;
  }

  return (
    <div
      className="modal-overlay"
      data-testid="refill-verify-modal"
      onClick={(e) => { if (e.target.classList.contains("modal-overlay")) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80,
      }}
    >
      <div
        className="modal-card"
        style={{
          background: "#fff", borderRadius: 18, padding: "22px 24px",
          maxWidth: 540, width: "100%", maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.30)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0F2A51", letterSpacing: "-0.01em" }}>
            Verify refill
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="refill-verify-close"
            className="btn btn--ghost btn--icon"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
          Scan the bottle's barcode to confirm this is the same prescription on file.
          {rx?.medName ? <> Saved: <strong>{rx.medName}</strong>{rx.rxNumber ? ` · Rx #${rx.rxNumber}` : ""}.</> : null}
        </div>

        {/* Scan input area */}
        {verdict.verdict === "no-scan" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <button
              type="button"
              className="btn btn--accent"
              onClick={() => setShowLive(true)}
              data-testid="refill-verify-camera"
              disabled={busy}
            >
              <Camera size={14} weight="bold" /> Live scan
            </button>
            <label className="btn btn--outline" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Barcode size={14} weight="bold" /> Upload photo
              <input type="file" accept="image/*" capture="environment" hidden onChange={handleFile} data-testid="refill-verify-upload" disabled={busy} />
            </label>
            <input
              type="text"
              className="field-input mono"
              placeholder="…or paste the barcode value"
              value={manualScan}
              onChange={(e) => setManualScan(e.target.value)}
              data-testid="refill-verify-manual"
              style={{ gridColumn: "1 / -1", fontSize: 13 }}
            />
          </div>
        )}

        {/* MATCH */}
        {verdict.verdict === "match" && (
          <VerdictPanel
            tone="green"
            icon={CheckCircle}
            title="Confirmed — barcode matches the bottle on file"
            body={`Saved barcode (${rx?.barcodeFormat || "unknown format"}) matches the scan. Confirming will bump "last filled" to today and decrement remaining refills.`}
            testId="refill-verdict-match"
          >
            <ScanReadout saved={verdict.savedNorm} scanned={verdict.scannedNorm} />
            <button
              type="button"
              className="btn btn--accent"
              onClick={confirmMatch}
              data-testid="refill-verify-confirm"
              disabled={busy}
              style={{ marginTop: 12 }}
            >
              <CheckCircle size={14} weight="bold" /> Confirm refill
            </button>
          </VerdictPanel>
        )}

        {/* MISMATCH */}
        {verdict.verdict === "mismatch" && (
          <VerdictPanel
            tone="red"
            icon={Warning}
            title="This barcode doesn't match the one on file"
            body="Possible mislabel, pharmacy swap, or wrong bottle. Do NOT take the medication until you've verified with the pharmacist."
            testId="refill-verdict-mismatch"
          >
            <ScanReadout saved={verdict.savedNorm} scanned={verdict.scannedNorm} highlight />
            {pharmacistMessage && (
              <div
                style={{
                  marginTop: 12, padding: "10px 12px",
                  background: "rgba(15,42,81,0.04)",
                  border: "1px solid rgba(15,42,81,0.18)",
                  borderRadius: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", color: "#475569" }}>
                    MESSAGE FOR THE PHARMACIST
                  </div>
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon"
                    title="Copy to clipboard"
                    data-testid="refill-pharmacist-copy"
                    onClick={() => {
                      try { navigator.clipboard.writeText(pharmacistMessage); showToast({ variant: "success", message: "Message copied" }); }
                      catch { /* clipboard blocked — silently ignore */ }
                    }}
                  >
                    <Copy size={13} weight="bold" />
                  </button>
                </div>
                <pre style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: "#0f172a", whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                  {pharmacistMessage}
                </pre>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn--accent"
                onClick={replaceBarcode}
                data-testid="refill-verify-replace"
                disabled={busy}
              >
                Replace barcode &amp; notify pharmacist
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setManualScan("")}
                data-testid="refill-verify-retry"
                disabled={busy}
              >
                Try another scan
              </button>
            </div>
          </VerdictPanel>
        )}

        {/* NO SAVED BARCODE — first-time adopt */}
        {verdict.verdict === "no-saved-barcode" && (
          <VerdictPanel
            tone="amber"
            icon={Warning}
            title="No barcode on file for this prescription yet"
            body="Save the scanned value as the canonical barcode so future refills can be verified automatically."
            testId="refill-verdict-no-saved"
          >
            <ScanReadout saved="(none on file)" scanned={verdict.scannedNorm} />
            <button
              type="button"
              className="btn btn--accent"
              onClick={adoptFirstBarcode}
              data-testid="refill-verify-adopt"
              disabled={busy}
              style={{ marginTop: 12 }}
            >
              Save barcode &amp; confirm refill
            </button>
          </VerdictPanel>
        )}
      </div>
    </div>
  );
}

// --- Small presentational helpers ---

function VerdictPanel({ tone, icon: Icon, title, body, children, testId }) {
  const palette = {
    green: { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.32)", text: "#047857" },
    red:   { bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.32)",  text: "#b91c1c" },
    amber: { bg: "rgba(217,119,6,0.10)",  border: "rgba(217,119,6,0.30)",  text: "#b45309" },
  }[tone];
  return (
    <div
      data-testid={testId}
      style={{
        padding: "14px 16px", borderRadius: 14,
        background: palette.bg, border: `1px solid ${palette.border}`,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <Icon size={20} weight="fill" color={palette.text} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>{title}</div>
          <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.55, marginTop: 4 }}>{body}</div>
          {children}
        </div>
      </div>
    </div>
  );
}

function ScanReadout({ saved, scanned, highlight }) {
  return (
    <div
      className="mono"
      style={{
        marginTop: 10, fontSize: 11.5, color: "#0f172a",
        background: "#fff", border: "1px solid rgba(15,42,81,0.12)", borderRadius: 8,
        padding: "8px 10px",
      }}
    >
      <div data-testid="refill-readout-saved">
        <strong style={{ color: "#475569" }}>ON FILE</strong>: {saved || "—"}
      </div>
      <div data-testid="refill-readout-scanned" style={{ marginTop: 2, color: highlight ? "#b91c1c" : "#0f172a" }}>
        <strong style={{ color: "#475569" }}>SCANNED</strong>: {scanned || "—"}
      </div>
    </div>
  );
}
